import { useContext } from 'react';
import { OverlayTriggerStateContext } from 'react-aria-components';
import { useForm } from 'react-hook-form';
import { Button, TextArea, TextField, toastQueue, ToggleIconButton } from '@launchpad-ui/components';

import { FeedbackSubmissionError, sendFeedbackToDestinations } from './sendFeedbackToDestinations';

import styles from './FeedbackForm.module.css';

const FEEDBACK_MAX_LENGTH = 3500;

interface FeedbackFormData {
  feedback: string;
  sessionReplayURL: string;
  sentiment: 'positive' | 'negative' | null;
}

export const FeedbackForm = ({ flagKey }: { flagKey: string }) => {
  const {
    clearErrors,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
    watch,
    setValue,
    trigger,
  } = useForm<FeedbackFormData>({
    mode: 'onSubmit',
    defaultValues: {
      sentiment: null,
    },
  });
  const overLayContext = useContext(OverlayTriggerStateContext);
  const sentiment = watch('sentiment');

  const setSentiment = async (s: 'positive' | 'negative') => {
    setValue('sentiment', sentiment === s ? null : s);
    await trigger();
  };

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      await sendFeedbackToDestinations({
        flagKey,
        feedback: data.feedback,
        sentiment: data.sentiment,
      });

      reset();
      toastQueue.add({
        title: `Feedback sent successfully`,
        status: 'success',
      });
      if (overLayContext) {
        overLayContext.close();
      }
    } catch (err) {
      setError('root', {
        message:
          err instanceof FeedbackSubmissionError
            ? err.message
            : 'An error occurred while sending feedback. Please try again.',
      });
    }
  };

  return (
    <div className={styles.feedbackForm}>
      <TextField aria-label="Feedback" className={styles.feedbackTextField}>
        <TextArea
          autoFocus
          {...register('feedback', {
            validate: (value, formValue) =>
              !!value || !!formValue.sentiment || 'Please give either a feedback or a thumbs up or down',
            setValueAs: (value: string) => value.trim(),
            minLength: { value: 10, message: 'Feedback must be at least 10 characters' },
            maxLength: {
              value: FEEDBACK_MAX_LENGTH,
              message: `Feedback is too long. Max ${FEEDBACK_MAX_LENGTH} characters.`,
            },
            onChange: () => {
              clearErrors('root');
            },
          })}
          placeholder="Tell us what you think..."
          className={styles.feedbackTextArea}
        />
        {errors.feedback && <span className={styles.errorMessage}>{errors.feedback.message}</span>}
        {errors.root && <span className={styles.errorMessage}>{errors.root.message}</span>}
      </TextField>
      <div className={styles.footer}>
        <div className={styles.emojisWrapper}>
          <label className={styles.sentimentLabel}>
            <input type="radio" {...register('sentiment')} value="positive" className={styles.hiddenRadio} />
            <ToggleIconButton
              icon="thumb-up-outline"
              aria-label="Thumbs up"
              isSelected={sentiment === 'positive'}
              onPress={() => setSentiment('positive')}
            />
          </label>

          <label className={styles.sentimentLabel}>
            <input type="radio" {...register('sentiment')} value="negative" className={styles.hiddenRadio} />
            <ToggleIconButton
              icon="thumb-down-outline"
              aria-label="Thumbs down"
              isSelected={sentiment === 'negative'}
              onPress={() => setSentiment('negative')}
            />
          </label>
        </div>
        <Button variant="primary" isDisabled={isSubmitting} onPress={() => handleSubmit(onSubmit)()}>
          {isSubmitting ? 'Sending...' : 'Submit'}
        </Button>
      </div>
    </div>
  );
};
